import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

interface FormData {
  homeAirport: string;
  destinationCity: string;
  tripsPerPeriod: string;
  tripFrequency: 'month' | 'quarter';
  numberOfTravelers: string;
  tripType: 'same-day' | 'overnight' | 'mixed';
  timeValue: string;
  jobTitle: string;
  teamSize: string;
  flexibilityImportance: 'critical' | 'important' | 'nice-to-have';
  commercialFriction: string[];
}

export default function TimeValuePage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [showResults, setShowResults] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    homeAirport: '',
    destinationCity: '',
    tripsPerPeriod: '',
    tripFrequency: 'month',
    numberOfTravelers: '',
    tripType: 'same-day',
    timeValue: '',
    jobTitle: '',
    teamSize: '',
    flexibilityImportance: 'important',
    commercialFriction: [],
  });

  const totalSteps = 9;

  const updateFormData = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleFriction = (value: string) => {
    setFormData(prev => ({
      ...prev,
      commercialFriction: prev.commercialFriction.includes(value)
        ? prev.commercialFriction.filter(v => v !== value)
        : [...prev.commercialFriction, value]
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleCalculate = () => {
    setShowResults(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetTool = () => {
    setShowResults(false);
    setCurrentStep(1);
    setFormData({
      homeAirport: '',
      destinationCity: '',
      tripsPerPeriod: '',
      tripFrequency: 'month',
      numberOfTravelers: '',
      tripType: 'same-day',
      timeValue: '',
      jobTitle: '',
      teamSize: '',
      flexibilityImportance: 'important',
      commercialFriction: [],
    });
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/time-value`,
      name: 'How Much Is Your Time Worth? | YESJETS Time Value Calculator',
      url: `${siteUrl}/time-value`,
      description: 'Calculate the true value of your time with our executive travel assessment tool. Discover how many hours you could recover annually with private aviation.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Time Value Calculator', item: `${siteUrl}/time-value` },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      '@id': `${siteUrl}/time-value#tool`,
      name: 'YESJETS Executive Time Value Calculator',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: 'An interactive multi-step assessment tool that calculates how many hours and how much value executives can recover annually by switching from commercial to private aviation.',
      provider: {
        '@type': 'Organization',
        name: 'YESJETS',
        url: siteUrl,
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
    },
  ];

  // Calculation logic
  const calculateResults = () => {
    const trips = parseInt(formData.tripsPerPeriod) || 0;
    const annualTrips = formData.tripFrequency === 'month' ? trips * 12 : trips * 4;
    const hourlyValue = parseInt(formData.timeValue) || 250;
    
    // Base time lost per commercial trip (hours)
    let hoursLostPerTrip = 4; // Base: security, boarding, delays
    
    if (formData.commercialFriction.includes('connections')) hoursLostPerTrip += 2;
    if (formData.commercialFriction.includes('long-drives')) hoursLostPerTrip += 1.5;
    if (formData.commercialFriction.includes('overnight-stays')) hoursLostPerTrip += 3;
    
    // Private aviation saves ~60-70% of that time
    const hoursSavedPerTrip = hoursLostPerTrip * 0.65;
    const annualHoursSaved = Math.round(hoursSavedPerTrip * annualTrips);
    const annualValueRecovered = Math.round(annualHoursSaved * hourlyValue);
    
    // Hotel nights saved (if overnight trips)
    const hotelNightsSaved = formData.tripType === 'overnight' || formData.tripType === 'mixed' 
      ? Math.round(annualTrips * 0.4) 
      : 0;
    
    return {
      annualTrips,
      hoursLostPerTrip: Math.round(hoursLostPerTrip * 10) / 10,
      hoursSavedPerTrip: Math.round(hoursSavedPerTrip * 10) / 10,
      annualHoursSaved,
      annualValueRecovered,
      hotelNightsSaved,
      hourlyValue,
    };
  };

  const results = showResults ? calculateResults() : null;

  if (showResults && results) {
    return (
      <>
        <SEOHead
          title="Your Time Value Results | YESJETS Private Aviation"
          description="Discover how much time and value you could recover by switching to private aviation. Calculate your annual hours saved and business opportunity gained."
          keywords="private jet time savings, executive time value, business aviation ROI, private flight calculator, time recovered"
          canonical="/time-value"
          jsonLd={jsonLd}
        />
        <Navbar />
        
        <main className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Results Header */}
            <div className="text-center mb-10 md:mb-12">
              <h1 className="text-3xl md:text-5xl font-serif text-slate-900 mb-4">
                Your Assessment Results
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-transparent via-gold to-transparent mx-auto mb-6"></div>
              <p className="text-base md:text-lg text-slate-700 leading-relaxed max-w-3xl mx-auto">
                Based on your travel profile, private aviation could save an estimated{' '}
                <strong className="text-gold">{results.annualHoursSaved} hours per year</strong> compared with traditional commercial travel.
                {formData.timeValue && (
                  <>
                    {' '}For an executive or business owner whose time is valued at{' '}
                    <strong className="text-gold">${results.hourlyValue}/hour</strong>, that represents roughly{' '}
                    <strong className="text-gold">${results.annualValueRecovered.toLocaleString()}</strong> in recovered time value
                  </>
                )}
                {' '}— before factoring in schedule flexibility, same-day return capability, team productivity, and access to airports closer to your destination.
              </p>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-10 md:mb-12">
              <div className="bg-white rounded-lg shadow-md p-5 md:p-6 border-t-4 border-gold">
                <div className="text-2xl md:text-3xl font-serif text-slate-900 mb-2">{results.annualHoursSaved}</div>
                <div className="text-xs md:text-sm text-slate-600 uppercase tracking-wider">Hours Saved Annually</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-5 md:p-6 border-t-4 border-gold">
                <div className="text-2xl md:text-3xl font-serif text-slate-900 mb-2">${results.annualValueRecovered.toLocaleString()}</div>
                <div className="text-xs md:text-sm text-slate-600 uppercase tracking-wider">Estimated Value Recovered</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-5 md:p-6 border-t-4 border-gold">
                <div className="text-2xl md:text-3xl font-serif text-slate-900 mb-2">{results.annualTrips}</div>
                <div className="text-xs md:text-sm text-slate-600 uppercase tracking-wider">Annual Trips</div>
              </div>
            </div>

            {/* Time Breakdown */}
            <div className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-10 md:mb-12">
              <h2 className="text-xl md:text-2xl font-serif text-slate-900 mb-6">Time Comparison Per Trip</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                  <div>
                    <div className="font-sans text-slate-900 mb-1">Commercial Aviation</div>
                    <div className="text-sm text-slate-600">Including security, connections, delays</div>
                  </div>
                  <div className="text-xl md:text-2xl font-serif text-red-600 ml-4 whitespace-nowrap">{results.hoursLostPerTrip} hrs</div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-sans text-slate-900 mb-1">Time Recovered with Private Aviation</div>
                    <div className="text-sm text-slate-600">Direct routing, minimal wait times</div>
                  </div>
                  <div className="text-xl md:text-2xl font-serif text-green-600 ml-4 whitespace-nowrap">{results.hoursSavedPerTrip} hrs</div>
                </div>
              </div>
            </div>

            {/* Where Value Comes From */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg shadow-xl p-6 md:p-10 mb-10 md:mb-12 text-white">
              <h2 className="text-2xl md:text-3xl font-serif mb-6 md:mb-8 text-center">Where the Value Comes From</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-gold/20 rounded-full flex-shrink-0">
                    <i className="ri-time-line text-xl md:text-2xl text-gold"></i>
                  </div>
                  <div>
                    <h3 className="font-sans text-base md:text-lg mb-2">Reduced Airport Time</h3>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      Arrive 15 minutes before departure. No security lines, no crowded terminals.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-gold/20 rounded-full flex-shrink-0">
                    <i className="ri-hotel-bed-line text-xl md:text-2xl text-gold"></i>
                  </div>
                  <div>
                    <h3 className="font-sans text-base md:text-lg mb-2">Fewer Overnight Stays</h3>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      {results.hotelNightsSaved > 0 
                        ? `Potentially eliminate ${results.hotelNightsSaved} hotel nights per year with same-day returns.`
                        : 'Complete same-day round trips that commercial schedules cannot support.'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-gold/20 rounded-full flex-shrink-0">
                    <i className="ri-map-pin-line text-xl md:text-2xl text-gold"></i>
                  </div>
                  <div>
                    <h3 className="font-sans text-base md:text-lg mb-2">Direct Access to Smaller Airports</h3>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      Land closer to your final destination. Reduce ground transportation time significantly.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-gold/20 rounded-full flex-shrink-0">
                    <i className="ri-calendar-check-line text-xl md:text-2xl text-gold"></i>
                  </div>
                  <div>
                    <h3 className="font-sans text-base md:text-lg mb-2">Same-Day Round Trips</h3>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      Attend morning meetings across the country and return home for dinner.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-gold/20 rounded-full flex-shrink-0">
                    <i className="ri-team-line text-xl md:text-2xl text-gold"></i>
                  </div>
                  <div>
                    <h3 className="font-sans text-base md:text-lg mb-2">More Productive Travel Time</h3>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      Private cabin for confidential discussions, presentations, and decision-making.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-gold/20 rounded-full flex-shrink-0">
                    <i className="ri-flashlight-line text-xl md:text-2xl text-gold"></i>
                  </div>
                  <div>
                    <h3 className="font-sans text-base md:text-lg mb-2">Schedule Flexibility</h3>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      Depart when you're ready. Adjust plans in real-time without rebooking penalties.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center">
              <h3 className="text-xl md:text-2xl font-serif text-slate-900 mb-4">Ready to Reclaim Your Time?</h3>
              <p className="text-slate-600 mb-6 max-w-2xl mx-auto text-sm md:text-base">
                Speak with a YESJETS charter advisor to discuss your specific travel needs and explore how private aviation can transform your business efficiency.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="/booking"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 bg-gold text-black rounded-full text-sm font-sans tracking-wider hover:bg-gold/90 transition-all cursor-pointer whitespace-nowrap shadow-lg"
                >
                  SPEAK WITH AN ADVISOR
                </a>
                <button
                  onClick={resetTool}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 border-2 border-slate-300 text-slate-700 rounded-full text-sm font-sans tracking-wider hover:border-slate-400 hover:bg-slate-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  RECALCULATE
                </button>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title="How Much Is Your Time Worth? | YESJETS Time Value Calculator"
        description="Calculate the true value of your time with our executive travel assessment tool. Discover how many hours you could recover annually with private aviation."
        keywords="executive time value, private jet calculator, business travel efficiency, time savings calculator, private aviation ROI"
        canonical="/time-value"
        jsonLd={jsonLd}
      />
      <Navbar />
      
      <main className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-20 md:pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          {/* Header */}
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-3xl md:text-5xl font-serif text-slate-900 mb-4">
              How Much Is Your Time Worth?
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-gold to-transparent mx-auto mb-6"></div>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              A consultative assessment to help you understand the cost of commercial travel and the value of time recovered through private aviation.
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-sans text-slate-600">Step {currentStep} of {totalSteps}</span>
              <span className="text-sm font-sans text-slate-600">{Math.round((currentStep / totalSteps) * 100)}%</span>
            </div>
            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-gold to-amber-500 transition-all duration-300 ease-out"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-lg shadow-lg p-6 md:p-10 mb-6">
            {/* Step 1: Home Airport */}
            {currentStep === 1 && (
              <div>
                <h2 className="text-xl md:text-2xl font-serif text-slate-900 mb-2">What is your home airport?</h2>
                <p className="text-sm text-slate-600 mb-6">The airport you typically depart from</p>
                <input
                  type="text"
                  value={formData.homeAirport}
                  onChange={(e) => updateFormData('homeAirport', e.target.value)}
                  placeholder="e.g., Chicago O'Hare (ORD)"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                />
              </div>
            )}

            {/* Step 2: Destination City */}
            {currentStep === 2 && (
              <div>
                <h2 className="text-xl md:text-2xl font-serif text-slate-900 mb-2">What is your typical destination city?</h2>
                <p className="text-sm text-slate-600 mb-6">Your most frequent business travel destination</p>
                <input
                  type="text"
                  value={formData.destinationCity}
                  onChange={(e) => updateFormData('destinationCity', e.target.value)}
                  placeholder="e.g., New York, Los Angeles, Miami"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                />
              </div>
            )}

            {/* Step 3: Trip Frequency */}
            {currentStep === 3 && (
              <div>
                <h2 className="text-xl md:text-2xl font-serif text-slate-900 mb-2">How often do you make this trip?</h2>
                <p className="text-sm text-slate-600 mb-6">Number of trips and frequency</p>
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <input
                    type="number"
                    value={formData.tripsPerPeriod}
                    onChange={(e) => updateFormData('tripsPerPeriod', e.target.value)}
                    placeholder="Number of trips"
                    min="1"
                    className="flex-1 px-4 py-3 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                  />
                  <select
                    value={formData.tripFrequency}
                    onChange={(e) => updateFormData('tripFrequency', e.target.value as 'month' | 'quarter')}
                    className="flex-1 px-4 py-3 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent cursor-pointer bg-white"
                  >
                    <option value="month">per month</option>
                    <option value="quarter">per quarter</option>
                  </select>
                </div>
              </div>
            )}

            {/* Step 4: Number of Travelers */}
            {currentStep === 4 && (
              <div>
                <h2 className="text-xl md:text-2xl font-serif text-slate-900 mb-2">How many people typically travel?</h2>
                <p className="text-sm text-slate-600 mb-6">Including yourself</p>
                <input
                  type="number"
                  value={formData.numberOfTravelers}
                  onChange={(e) => updateFormData('numberOfTravelers', e.target.value)}
                  placeholder="e.g., 1, 2, 4"
                  min="1"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                />
              </div>
            )}

            {/* Step 5: Trip Type */}
            {currentStep === 5 && (
              <div>
                <h2 className="text-xl md:text-2xl font-serif text-slate-900 mb-2">Are your trips usually same-day or overnight?</h2>
                <p className="text-sm text-slate-600 mb-6">Select the option that best describes your typical travel pattern</p>
                <div className="space-y-3">
                  {[
                    { value: 'same-day', label: 'Same-day (return home the same day)' },
                    { value: 'overnight', label: 'Overnight (require hotel stays)' },
                    { value: 'mixed', label: 'Mixed (varies by trip)' },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.tripType === option.value
                          ? 'border-gold bg-gold/5'
                          : 'border-slate-300 hover:border-slate-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="tripType"
                        value={option.value}
                        checked={formData.tripType === option.value}
                        onChange={(e) => updateFormData('tripType', e.target.value)}
                        className="w-5 h-5 text-gold cursor-pointer flex-shrink-0"
                      />
                      <span className="text-slate-900 text-sm md:text-base">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Step 6: Time Value */}
            {currentStep === 6 && (
              <div>
                <h2 className="text-xl md:text-2xl font-serif text-slate-900 mb-2">What is the approximate hourly value of your time?</h2>
                <p className="text-sm text-slate-600 mb-6">Or provide your job title / business type below</p>
                <input
                  type="number"
                  value={formData.timeValue}
                  onChange={(e) => updateFormData('timeValue', e.target.value)}
                  placeholder="e.g., 250, 500, 1000"
                  min="0"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent mb-4"
                />
                <div className="text-center text-slate-500 text-sm mb-4">— OR —</div>
                <input
                  type="text"
                  value={formData.jobTitle}
                  onChange={(e) => updateFormData('jobTitle', e.target.value)}
                  placeholder="e.g., CEO, Managing Partner, Business Owner"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                />
              </div>
            )}

            {/* Step 7: Team Size */}
            {currentStep === 7 && (
              <div>
                <h2 className="text-xl md:text-2xl font-serif text-slate-900 mb-2">How many people on your team usually travel?</h2>
                <p className="text-sm text-slate-600 mb-6">Total team members who travel for business</p>
                <input
                  type="number"
                  value={formData.teamSize}
                  onChange={(e) => updateFormData('teamSize', e.target.value)}
                  placeholder="e.g., 3, 5, 10"
                  min="1"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                />
              </div>
            )}

            {/* Step 8: Schedule Flexibility */}
            {currentStep === 8 && (
              <div>
                <h2 className="text-xl md:text-2xl font-serif text-slate-900 mb-2">How important is schedule flexibility to you?</h2>
                <p className="text-sm text-slate-600 mb-6">Ability to adjust departure times and respond to changing business needs</p>
                <div className="space-y-3">
                  {[
                    { value: 'critical', label: 'Critical — My schedule changes frequently' },
                    { value: 'important', label: 'Important — I value the option to adjust' },
                    { value: 'nice-to-have', label: 'Nice to have — My schedule is usually fixed' },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.flexibilityImportance === option.value
                          ? 'border-gold bg-gold/5'
                          : 'border-slate-300 hover:border-slate-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="flexibility"
                        value={option.value}
                        checked={formData.flexibilityImportance === option.value}
                        onChange={(e) => updateFormData('flexibilityImportance', e.target.value)}
                        className="w-5 h-5 text-gold cursor-pointer flex-shrink-0"
                      />
                      <span className="text-slate-900 text-sm md:text-base">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Step 9: Commercial Friction */}
            {currentStep === 9 && (
              <div>
                <h2 className="text-xl md:text-2xl font-serif text-slate-900 mb-2">What challenges do you face with commercial travel?</h2>
                <p className="text-sm text-slate-600 mb-6">Select all that apply</p>
                <div className="space-y-3">
                  {[
                    { value: 'connections', label: 'Requires connections or layovers' },
                    { value: 'long-drives', label: 'Long drives to/from airports' },
                    { value: 'overnight-stays', label: 'Forces overnight stays when same-day would be ideal' },
                    { value: 'delays', label: 'Frequent delays and cancellations' },
                    { value: 'limited-schedules', label: 'Limited flight schedules' },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.commercialFriction.includes(option.value)
                          ? 'border-gold bg-gold/5'
                          : 'border-slate-300 hover:border-slate-400'
                      }`}
                    >
                      <input
                        type="checkbox"
                        value={option.value}
                        checked={formData.commercialFriction.includes(option.value)}
                        onChange={() => toggleFriction(option.value)}
                        className="w-5 h-5 text-gold cursor-pointer rounded flex-shrink-0"
                      />
                      <span className="text-slate-900 text-sm md:text-base">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`inline-flex items-center gap-2 px-5 md:px-6 py-3 rounded-full text-sm font-sans tracking-wider transition-all whitespace-nowrap ${
                currentStep === 1
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : 'bg-slate-700 text-white hover:bg-slate-800 cursor-pointer'
              }`}
            >
              <i className="ri-arrow-left-line"></i>
              BACK
            </button>

            {currentStep < totalSteps ? (
              <button
                onClick={nextStep}
                className="inline-flex items-center gap-2 px-5 md:px-6 py-3 bg-gold text-black rounded-full text-sm font-sans tracking-wider hover:bg-gold/90 transition-all cursor-pointer whitespace-nowrap"
              >
                NEXT
                <i className="ri-arrow-right-line"></i>
              </button>
            ) : (
              <button
                onClick={handleCalculate}
                className="inline-flex items-center gap-2 px-6 md:px-8 py-3 bg-gradient-to-r from-gold to-amber-500 text-black rounded-full text-sm font-sans tracking-wider hover:from-gold/90 hover:to-amber-500/90 transition-all cursor-pointer whitespace-nowrap shadow-lg"
              >
                SEE MY RESULTS
                <i className="ri-calculator-line"></i>
              </button>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}